import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { EmptyState } from '../components/ui/EmptyState';
import { Loading } from '../components/ui/Loading';
import { formatDate, formatCurrency } from '../lib/utils';
import { useRisks } from '../hooks/useRisks';

const Risks: React.FC = () => {
    const [filters, setFilters] = useState({
        severity: '',
        status: '',
        type: '',
    });

    const { data, isLoading } = useRisks(filters);

    if (isLoading) {
        return <Loading />;
    }

    const risks = data?.data || [];
    const criticalCount = data?.critical_count || 0;
    const highCount = data?.high_count || 0;

    return (
        <div className="space-y-6">
            {/* Header */}
            <div>
                <h1 className="text-2xl font-bold text-gray-900">Risk Flags</h1>
                <p className="text-gray-600 mt-1">Monitor and manage detected payment risks</p>
            </div>

            {/* Filters */}
            <Card>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <select
                        value={filters.severity}
                        onChange={(e) => setFilters((prev) => ({ ...prev, severity: e.target.value }))}
                        className="input"
                    >
                        <option value="">All Severities</option>
                        <option value="critical">Critical</option>
                        <option value="high">High</option>
                        <option value="medium">Medium</option>
                        <option value="low">Low</option>
                    </select>

                    <select
                        value={filters.status}
                        onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value }))}
                        className="input"
                    >
                        <option value="">All Statuses</option>
                        <option value="pending">Pending</option>
                        <option value="resolved">Resolved</option>
                    </select>

                    <select
                        value={filters.type}
                        onChange={(e) => setFilters((prev) => ({ ...prev, type: e.target.value }))}
                        className="input"
                    >
                        <option value="">All Types</option>
                        <option value="lost_revenue">Lost Revenue</option>
                        <option value="duplicate_revenue">Duplicate Revenue</option>
                        <option value="amount_mismatch">Amount Mismatch</option>
                        <option value="invalid_state_flow">Invalid State Flow</option>
                        <option value="duplicate_state">Duplicate State</option>
                        <option value="provider_without_init">Provider Without Init</option>
                    </select>

                    <button
                        onClick={() => setFilters({ severity: '', status: '', type: '' })}
                        className="btn-secondary"
                    >
                        Clear Filters
                    </button>
                </div>
            </Card>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                {[
                    { label: 'Critical', count: criticalCount, color: 'text-severity-critical bg-severity-critical-bg' },
                    { label: 'High', count: highCount, color: 'text-severity-high bg-severity-high-bg' },
                    { label: 'Medium', count: risks.filter(r => r.severity === 'medium').length, color: 'text-severity-medium bg-severity-medium-bg' },
                    { label: 'Low', count: risks.filter(r => r.severity === 'low').length, color: 'text-severity-low bg-severity-low-bg' },
                ].map((stat) => (
                    <Card key={stat.label} className="text-center">
                        <div className={`inline-flex items-center justify-center w-12 h-12 rounded-full ${stat.color} mb-3`}>
                            <span className="text-xl font-bold">{stat.count}</span>
                        </div>
                        <p className="text-sm font-medium text-gray-600">{stat.label} Risks</p>
                    </Card>
                ))}
            </div>

            {/* Risks List */}
            {risks.length === 0 ? (
                <Card>
                    <EmptyState
                        icon={AlertTriangle}
                        title="No risks found"
                        description="No risk flags match your current filters. Try adjusting your criteria."
                    />
                </Card>
            ) : (
                <div className="space-y-4">
                    {risks.map((risk) => (
                        <Card key={risk.id} className="hover:shadow-md transition-shadow">
                            <div className="flex items-start justify-between mb-4">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-red-50 rounded-lg">
                                        <AlertTriangle className="w-6 h-6 text-red-600" />
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-900">
                                            {risk.type.replace(/_/g, ' ')}
                                        </h3>
                                        <p className="text-sm text-gray-600 mt-1">{risk.description}</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Badge variant="severity" severity={risk.severity}>
                                        {risk.severity}
                                    </Badge>
                                    <Badge>{risk.resolved ? 'Resolved' : 'Pending'}</Badge>
                                </div>
                            </div>

                            <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                                <div className="flex items-center gap-6">
                                    {risk.payment && (
                                        <>
                                            <div>
                                                <p className="text-xs text-gray-500">Payment</p>
                                                <Link
                                                    to={`/payments/${risk.payment.id}`}
                                                    className="text-sm font-mono text-primary-600 hover:text-primary-700"
                                                >
                                                    {risk.payment.id.substring(0, 12)}...
                                                </Link>
                                            </div>
                                            <div>
                                                <p className="text-xs text-gray-500">Amount</p>
                                                <p className="text-sm font-semibold text-gray-900">
                                                    {formatCurrency(risk.payment.expected_amount, risk.payment.currency)}
                                                </p>
                                            </div>
                                            <div>
                                                <p className="text-xs text-gray-500">Provider</p>
                                                <p className="text-sm text-gray-900">{risk.payment.provider_label}</p>
                                            </div>
                                        </>
                                    )}
                                    <div>
                                        <p className="text-xs text-gray-500">Loss Estimate</p>
                                        <p className="text-sm font-semibold text-red-600">
                                            {risk.estimated_loss > 0 ? formatCurrency(risk.estimated_loss) : 'N/A'}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-500">Confidence</p>
                                        <p className="text-sm font-semibold text-gray-900">
                                            {(risk.confidence_score * 100).toFixed(0)}%
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-500">Detected</p>
                                        <p className="text-sm text-gray-900">{formatDate(risk.detected_at)}</p>
                                    </div>
                                </div>
                                {risk.payment && (
                                    <Link to={`/payments/${risk.payment.id}`} className="btn-primary">
                                        View Details
                                    </Link>
                                )}
                            </div>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
};

export default Risks;
